import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, CheckBox, Modal } from '@iwoca/orion';
import { Input } from '@iwoca/orion-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './AgreeDirectDebitMandateModal.module.css';
import { REGISTER_DE_BANK_ACCOUNT } from '../addPaymentMethodModal/AddPaymentMethodModal.actions';
import { validateIBAN } from '../addPaymentMethodModal/linkBankAccount/formValidation/IBANFormValidation';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    addBankAccount: (bankAccountDetails: {
        iban: string;
        accountNumber: string;
        sortCode: string;
    }) =>
        dispatch({
            type: REGISTER_DE_BANK_ACCOUNT,
            ...bankAccountDetails
        })
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

type TProps = {
    previouslyVerifiedIBAN?: string;
    onClose: () => void;
    isModalOpen: boolean;
};

const AgreeDirectDebitMandateModal = ({
    previouslyVerifiedIBAN,
    onClose,
    isModalOpen,
    addBankAccount
}: TProps & PropsFromRedux) => {
    const [directDebitAgreed, setDirectDebitModalAgreed] = useState(false);
    const [newBankAccountIban, setNewBankAccountIban] = useState(
        previouslyVerifiedIBAN || ''
    );
    const [failedValidation, setFailedVaildation] = useState(false);
    const handleCloseModal = () => {
        setNewBankAccountIban('');
        setFailedVaildation(false);
        onClose();
    };

    const handleAgreeCheckBoxClick = (checked: boolean) => {
        setDirectDebitModalAgreed(checked);
    };

    const handleSubmitDirectDebitMandate = (event: React.FormEvent) => {
        tracking.ampli.addBankDetailsIbanSubmitted({
            new_iban_submitted: !previouslyVerifiedIBAN
        });
        event.preventDefault();

        if (previouslyVerifiedIBAN) {
            addBankAccount({
                iban: previouslyVerifiedIBAN,
                accountNumber: '',
                sortCode: ''
            });
            return handleCloseModal();
        }
        const isIbanValid =
            !!newBankAccountIban && validateIBAN({ iban: newBankAccountIban });

        if (!isIbanValid) {
            return setFailedVaildation(true);
        }
        addBankAccount({
            iban: newBankAccountIban,
            accountNumber: '',
            sortCode: ''
        });

        handleCloseModal();
    };

    return (
        <Modal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            closeIcon={true}
            headerBar={true}
            title={
                <FormattedMessage
                    id="_.modal.agree_direct_debit_mandate.title"
                    defaultMessage="SEPA Core Direct Debit Mandate"
                />
            }
        >
            <div className={styles.AgreeDirectDebitMandateModal}>
                <div>
                    <form
                        className={styles.agreeDirectDebitMandateForm}
                        onSubmit={handleSubmitDirectDebitMandate}
                    >
                        <div className={styles.formScrollableSection}>
                            <IBANEntryComponent
                                submittedIban={previouslyVerifiedIBAN}
                                setIban={setNewBankAccountIban}
                                currentIban={newBankAccountIban}
                                failedValidation={failedValidation}
                            />
                            <div className={styles.agreementTextContainer}>
                                <>
                                    <p className={styles.copyParagraph}>
                                        {`Ich ermächtige iwoca Management S.à.r.l.
                            (Gläubiger-ID: LU72ZZZ0000000000000001848) und
                            Stripe Payments Europe Ltd., unseren
                            Zahlungsdienstleister, Zahlungen von meinem oben
                            angegebenen Konto mittels Lastschrift einzuziehen.
                            Zugleich weise ich mein Kreditinstitut an, die von
                            Stripe Payments Europe Ltd. im Auftrag von iwoca
                            Management S.à.r.l. auf mein Konto gezogenen
                            Lastschriften einzulösen.`}
                                    </p>
                                    <br />
                                    <p
                                        className={styles.copyParagraph}
                                    >{`Weiterhin erkläre ich mich damit einverstanden, die
                            Vorabankündigung bis spätestens 3 Tage vor
                            Zahlungseinzug zu erhalten. Meine Rechte kann ich
                            den Bedingungen meines Kreditinstituts entnehmen.`}</p>
                                    <br />
                                    <p className={styles.copyParagraph}>
                                        {`Hinweis: Ich kann innerhalb von acht Wochen,
                                beginnend mit dem Belastungsdatum, die Erstattung
                                des belasteten Betrages verlangen. Es gelten dabei
                                die mit meinem Kreditinstitut vereinbarten
                                Bedingungen.`}
                                    </p>
                                    <br />
                                    <p
                                        className={styles.copyParagraph}
                                        data-testid="stripe-link-information-sentence"
                                    >
                                        Für mehr Informationen lesen Sie gern
                                        auch den{' '}
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://stripe.com/en-gb-de/privacy#translation"
                                            className={styles.copyLink}
                                        >
                                            Datenschutzhinweis
                                        </a>{' '}
                                        von Stripe.
                                    </p>
                                </>
                            </div>
                        </div>
                        <div className={styles.checkboxWrapper}>
                            <CheckBox
                                name="directDebitMandate"
                                required
                                aria-label="agree"
                                onChange={(e) =>
                                    handleAgreeCheckBoxClick(e.target.checked)
                                }
                            />
                            <label htmlFor="directDebitMandate">
                                <FormattedMessage
                                    id="_.modal.agree_direct_debit_mandate.checkboxMessage"
                                    defaultMessage="I have read and understood the SEPA Direct Debit Mandate and hereby give my consent to the recurring debit."
                                />
                            </label>
                        </div>
                        <div className={styles.buttonWrapper}>
                            <Button
                                type="submit"
                                disabled={
                                    !directDebitAgreed ||
                                    (!previouslyVerifiedIBAN &&
                                        !newBankAccountIban)
                                }
                                className={styles.submitButton}
                            >
                                <FormattedMessage
                                    id="_.modal.agree_direct_debit_mandate.submit"
                                    defaultMessage="Submit"
                                />
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(AgreeDirectDebitMandateModal);

const IBANEntryComponent = ({
    submittedIban,
    currentIban,
    setIban,
    failedValidation
}: {
    submittedIban?: string;
    currentIban: string;
    setIban: (iban: string) => void;
    failedValidation: boolean;
}) => {
    const intl = useIntl();

    if (submittedIban) {
        return (
            <>
                <p className={styles.verifiedIbanHeader}>
                    <FormattedMessage
                        id="_.funding_requirements.add_bank_details.add_new_account_submitted_iban"
                        defaultMessage="Your account's IBAN: "
                    />
                </p>
                <Input type="text" value={submittedIban} disabled={true} />
            </>
        );
    }
    return (
        <>
            <p className={styles.inputHeader}>
                <FormattedMessage
                    id="_.funding_requirements.add_bank_details.add_new_account_input_header"
                    defaultMessage="Enter your account’s IBAN:"
                />
            </p>
            <p className={styles.inputSubHeader}>
                <FormattedMessage
                    id="_.funding_requirements.add_bank_details.add_new_account_input_subheader"
                    defaultMessage="Consisting of 22 characters (2-digit country code + 2-digit check number,
8-digit bank code + 10-digit account number)"
                />
            </p>
            <div>
                <Input
                    type="text"
                    name="iban"
                    placeholder={intl.formatMessage({
                        id: '_.funding_requirements.add_bank_details.add_new_account_placeholder_messsage',
                        defaultMessage: 'Your IBAN...'
                    })}
                    value={currentIban}
                    onChange={(e) => {
                        setIban(e.target.value);
                    }}
                />
                {failedValidation && (
                    <p className={styles.ibanError}>
                        <FormattedMessage
                            id="_.funding_requirements.add_bank_details.add_new_account_input_error_message"
                            defaultMessage="Invalid IBAN"
                        />
                    </p>
                )}
            </div>
        </>
    );
};
